import { IcChevronDown, IcChevronRight } from '@assets/icons/24x24';
import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { FunctionComponent, ReactNode, SVGProps, useState } from 'react';

//This component can open a link or display children, but not both
type AdditionalProps =
  | {
      children: ReactNode;
      onClick?: never;
    }
  | {
      children?: never;
      onClick: () => void;
    };

type GlobalProps = {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  label: string;
  optionalLabel?: string;
  optionalLabelColor?: string;
  arrowOrientation?: 'right' | 'bottom';
  isDisabled?: boolean;
};

type Props = GlobalProps & AdditionalProps;

export default function DataSheetCardLink({
  icon,
  label,
  children,
  onClick,
  optionalLabel,
  arrowOrientation = 'right',
  optionalLabelColor = 'neutral.300',
  isDisabled = false,
}: Readonly<Props>) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <VStack gap={0} width="100%" className="group" backgroundColor="neutral.700">
      <HStack
        paddingLeft="standardM"
        paddingRight={2.5}
        gap="standardXS"
        height={{ base: '64px', '4k': '80px' }}
        width="100%"
        userSelect="none"
        backgroundColor="neutral.700"
        _hover={isDisabled ? undefined : { backgroundColor: 'blue.700', cursor: 'pointer' }}
        onClick={() => {
          if (isDisabled) {
            return;
          }
          onClick ? onClick() : setIsOpen((prev) => !prev);
        }}
      >
        <Icon
          as={icon}
          boxSize={{ base: '24px', '4k': '32px' }}
          color="neutral.300"
          _groupHover={isDisabled ? undefined : { color: 'skyBlue' }}
        />
        <Text flex={1} textStyle="screenStd">
          {label}
        </Text>
        {optionalLabel && (
          <Text
            marginLeft="auto"
            backgroundColor="neutral.black"
            paddingX="tightM"
            paddingY="tightXS"
            textStyle="screenStd"
            fontWeight="medium"
            color={optionalLabelColor}
            lineClamp={1}
            flexShrink={0}
          >
            {optionalLabel}
          </Text>
        )}
        <Icon
          as={arrowOrientation === 'bottom' ? IcChevronDown : IcChevronRight}
          boxSize={{ base: '24px', '4k': '32px' }}
          color="neutral.white"
          _groupHover={isDisabled ? undefined : { color: 'skyBlue' }}
          transform={isOpen ? 'rotate(180deg)' : ''}
        />
      </HStack>
      {isOpen && children}
    </VStack>
  );
}
